import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, debounceTime, map, of, switchMap, tap } from 'rxjs';
import * as TotalGrossActions from '../../finances/store/total-gross/total-gross.actions';
import { VenueFinancesService } from '../../venue/services/venue-finances.service';
import { SalesService } from '../services/sales.service';
import * as SaleActions from './sales.actions';

@Injectable()
export class SalesEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private _logger = inject(LoggerService);
    private _saleService = inject(SalesService);
    private _alertService = inject(AlertService);
    private _venueFinancesService = inject(VenueFinancesService);

    messages = {
        getAllError: 'Error getting all sales',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    actionsToListen = [
        TotalGrossActions.customTotalGrossValidation,
        TotalGrossActions.updateTotalGrossByLocationSuccess,
        TotalGrossActions.updateTotalGrossBySalesRepSuccess,
    ];

    // ------------------------------------------------------------------------------------------
    // @ Effects
    // ------------------------------------------------------------------------------------------

    validateSalesConsistency$ = createEffect(() =>
        this._actions$.pipe(
            ofType(...this.actionsToListen),
            debounceTime(300),
            switchMap((action) => {
                return this._saleService
                    .validateSales(
                        action.playdateId,
                        action.saleType,
                        action.departure
                    )
                    .pipe(
                        map((res) => {
                            const data = {
                                entitiesWithoutTotalGross:
                                    res.data.entities_without_total_gross,
                                entitiesWithGrossMismatch:
                                    res.data.entities_with_gross_mismatch,
                                resource: action.resource,
                            };

                            return SaleActions.validateSalesConsistencySuccess({
                                consistencies: data,
                            });
                        }),
                        catchError((error) =>
                            of(
                                SaleActions.loadSalesFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    onValidateSalesConsistencySuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(SaleActions.validateSalesConsistencySuccess),
                tap((action) => {
                    // Reload totals
                    this._venueFinancesService.onUpdateFinances = true;
                })
            ),
        { dispatch: false }
    );

    // // Get All Sales
    // getAllSales$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(SaleActions.loadSales),
    //         switchMap(() => {
    //             return this._saleService.getAll().pipe(
    //                 map((res) => {
    //                     return SaleActions.loadSalesSuccess({
    //                         sales: res.data,
    //                     });
    //                 }),
    //                 catchError((error) =>
    //                     of(
    //                         SaleActions.loadSalesFailure({
    //                             error:
    //                                 error?.error?.message ||
    //                                 this.messages.unknownError,
    //                         })
    //                     )
    //                 )
    //             );
    //         })
    //     )
    // );

    // // Get One Sale
    // getSale$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(SaleActions.loadSale),
    //         switchMap((action) => {
    //             return this._saleService.getOne(action.id).pipe(
    //                 map((res) => {
    //                     return SaleActions.loadSaleSuccess({
    //                         sale: res.data,
    //                     });
    //                 }),
    //                 catchError((error) =>
    //                     of(
    //                         SaleActions.loadSaleFailure({
    //                             error:
    //                                 error?.error?.message ||
    //                                 this.messages.unknownError,
    //                         })
    //                     )
    //                 )
    //             );
    //         })
    //     )
    // );

    // // Create Sale
    // createSale$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(SaleActions.createSale),
    //         switchMap((action) => {
    //             return this._saleService.create(action.data).pipe(
    //                 map((res) => {
    //                     return SaleActions.createSaleSuccess({
    //                         sale: res.data,
    //                         success: 'Sale created successfully',
    //                     });
    //                 }),
    //                 catchError((error) =>
    //                     of(
    //                         SaleActions.createSaleFailure({
    //                             error:
    //                                 error?.error?.message ||
    //                                 this.messages.unknownError,
    //                         })
    //                     )
    //                 )
    //             );
    //         })
    //     )
    // );

    // // Update Sale
    // updateSale$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(SaleActions.updateSale),
    //         switchMap((action) => {
    //             return this._saleService.update(action.data, action.id).pipe(
    //                 map((res) => {
    //                     return SaleActions.updateSaleSuccess({
    //                         sale: res.data,
    //                         success: 'Sale updated successfully',
    //                     });
    //                 }),
    //                 catchError((error) =>
    //                     of(
    //                         SaleActions.updateSaleFailure({
    //                             error:
    //                                 error?.error?.message ||
    //                                 this.messages.unknownError,
    //                         })
    //                     )
    //                 )
    //             );
    //         })
    //     )
    // );

    // // Delete Sale
    // deleteSale$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(SaleActions.deleteSale),
    //         switchMap((action) => {
    //             return this._saleService.delete(action.id).pipe(
    //                 map(() => {
    //                     return SaleActions.deleteSaleSuccess({
    //                         id: action.id,
    //                         success: 'Sale deleted successfully',
    //                     });
    //                 }),
    //                 catchError((error) =>
    //                     of(
    //                         SaleActions.deleteSaleFailure({
    //                             error:
    //                                 error?.error?.message ||
    //                                 this.messages.unknownError,
    //                         })
    //                     )
    //                 )
    //             );
    //         })
    //     )
    // );

    // ------------------------------------------------------------------------------------------
    // @ Alerts
    // ------------------------------------------------------------------------------------------

    onSaleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SaleActions.createSaleSuccess,
                    SaleActions.updateSaleSuccess,
                    SaleActions.deleteSaleSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onSaleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SaleActions.loadSalesFailure,
                    SaleActions.loadSaleFailure,
                    SaleActions.createSaleFailure,
                    SaleActions.updateSaleFailure,
                    SaleActions.deleteSaleFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
